import React, { useState } from "react";
import { useDispatch } from "react-redux";
import classnames from "classnames";
import PropTypes from "prop-types";
import formatUSD from "utils/formatUSD";
import { Image } from "@sparefoot/react-commons";

import { ClickToCall } from "components/brand/ClickToCall";
import { Button } from "components/core/Button";
import { Link } from "components/core/Link";
import { Text, H3 } from "components/core/Typography";
import { MapPinFilled } from "components/core/Icons";
import { StarReviewsCount } from "components/molecules/review/StarReviewsCount";
import { getLocalNumber } from "store/modules/phone";
import "./FacilityCard.scss";
import FeeTooltip from "components/facility/FeeTooltip/FeeTooltip";
import { trackCTAClick } from "hooks/tracking/utils/clickTrackingUtils";
import OMIBanner from "components/organisms/search/OMIBanner/OMIBanner";
import DealBanner from "components/organisms/search/DealBanner/DealBanner";
import { GTM_CLASSNAMES } from "config/gtmSelectors";

const FacilityTitle = ({ name, address, url }) => (
	<div className="facility-title">
		<Link
			href={url}
			last
			display="block"
			weight="bold"
			color="black"
		>
			<H3
				className="facility-name"
				weight="bold"
				size="medium"
				color="darkBlue"
			>
				{name}
			</H3>
		</Link>

		<Text
			last
			display="block"
			className="facility-address"
			mediumSize="small"
		>
			{address}
		</Text>
	</div>
);
FacilityTitle.propTypes = {
	name: PropTypes.string,
	address: PropTypes.string,
	url: PropTypes.string
};

const FacilityDistance = ({ distance }) => (
	<div className="facility-distance">
		<MapPinFilled fill="blue" />
		<Text
			last
			size="small"
			className="distance-text"
		>
			<span className="distance-number">{distance}</span>
			<span className="distance-label"> miles away</span>
		</Text>
	</div>
);
FacilityDistance.propTypes = {
	distance: PropTypes.number
};

const FacilityImage = ({
	src,
	showDefaultImage,
	alt,
	hasOnlineMoveins,
	isAboveFold
}) => (
	<div
		className={classnames("facility-image", {
			"show-default": showDefaultImage
		})}
	>
		<Image
			src={src}
			alt={alt}
			loading={isAboveFold ? "eager" : "lazy"}
			fetchPriority={isAboveFold ? "high" : undefined}
		/>
		{showDefaultImage && (
			<div className="default-image">
				<Text
					last
					weight="bold"
					color="white"
				>
					No facility image
				</Text>
			</div>
		)}
		{hasOnlineMoveins && (
			<div className="omi-banner-overlay">
				<OMIBanner />
			</div>
		)}
	</div>
);

FacilityImage.propTypes = {
	showDefaultImage: PropTypes.bool,
	src: PropTypes.string,
	alt: PropTypes.string,
	hasOnlineMoveins: PropTypes.bool,
	isAboveFold: PropTypes.bool
};

const FacilityPrice = ({
	startingPrice,
	lowestPrice,
	totalFees,
	fees,
	showFeeWarning
}) => {
	const showStartingPrice = startingPrice && lowestPrice < startingPrice;
	return (
		<div className="facility-price">
			<Text
				last
				size="small"
				className="facility-price-text"
			>
				Starting at
			</Text>
			<div className="prices">
				<Text
					last
					color="green"
					className="lowest-price"
				>
					{formatUSD(lowestPrice, { showDecimal: true })}
				</Text>
				{showStartingPrice && (
					<Text
						last
						size="small"
						strikethrough
						className="starting-price"
					>
						{formatUSD(startingPrice)}
					</Text>
				)}
			</div>
			{showFeeWarning && totalFees > 0 && (
				<FeeTooltip
					fees={fees}
					totalFees={totalFees}
				/>
			)}
		</div>
	);
};
FacilityPrice.propTypes = {
	startingPrice: PropTypes.number,
	lowestPrice: PropTypes.number,
	fees: PropTypes.arrayOf(PropTypes.object),
	showFeeWarning: PropTypes.bool,
	totalFees: PropTypes.number
};

const ReserveNowButton = ({ url }) => {
	const handleViewUnits = () => {
		trackCTAClick(
			"view-all-units",
			"View all units/prices Button",
			"View All Units",
			url
		);
		globalThis.location.assign(url);
	};
	return (
		<Button
			href={url}
			onClick={handleViewUnits}
			theme="primary"
			className={classnames(
				"reserve-button",
				GTM_CLASSNAMES.FACILITY_CARD_VIEW_ALL_UNITS
			)}
		>
			View All Units
		</Button>
	);
};
ReserveNowButton.propTypes = {
	url: PropTypes.string
};

// --------------------------------------------------
const FacilityCard = ({
	name,
	layout = "wide",
	image,
	showDefaultImage,
	address,
	distance,
	url,
	lowestPrice,
	startingPrice,
	reviews = {
		average: 0,
		number: 0
	},
	schema,
	totalFees,
	lockFee,
	adminFee,
	showFeeWarning,
	hasOnlineMoveins,
	dealBannerText,
	subPageType,
	cardIndex
}) => {
	const showDealBanner = subPageType === "deals";
	// First 3 cards are typically above the fold on both desktop and mobile
	const isAboveFold = cardIndex < 3;
	const [phone, setPhone] = useState({});
	const dispatch = useDispatch();
	const handleFetchNumber = async ({ city, state }) => {
		const data = { path: `${city}/${state}` };
		try {
			const response = await dispatch(getLocalNumber(data));

			// Check if response has the expected structure
			if (response?.payload?.result) {
				setPhone(response.payload.result);
				return response.payload.result;
			}

			// Return null for unexpected response structure
			return null;
		} catch (error) {
			// Return null on error - the Redux store will handle error state
			return null;
		}
	};

	const fees = [
		{
			name: "One-Time Admin Fee",
			price: adminFee
		},
		{
			name: "One-Time Lock Fee",
			price: lockFee
		}
	];

	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<div className="facility-card">
			<div className="card-main">
				{showDealBanner && dealBannerText && (
					<DealBanner text={dealBannerText} />
				)}
				<div
					className={classnames("card-top", `card-layout-${layout}`)}
				>
					<FacilityImage
						src={image}
						showDefaultImage={showDefaultImage}
						alt={name}
						hasOnlineMoveins={hasOnlineMoveins}
						isAboveFold={isAboveFold}
					/>
					<div className="review-section">
						{distance > 0 && (
							<FacilityDistance distance={distance} />
						)}
						{reviews.number > 0 && (
							<StarReviewsCount
								className="facility-reviews"
								url={`${url}#reviews`}
								number={reviews.number}
								average={reviews.average}
								withLabel
								facilityCard
							/>
						)}
					</div>
					<FacilityTitle
						url={url}
						name={name}
						address={address}
					/>
					<ClickToCall
						underline
						handleFetchNumber={() =>
							handleFetchNumber(schema?.location)
						}
						phone={phone}
						textFontWeight="bold"
					/>
				</div>
				<div className="card-bottom">
					<FacilityPrice
						startingPrice={startingPrice}
						lowestPrice={lowestPrice}
						fees={fees}
						totalFees={totalFees}
						showFeeWarning={showFeeWarning}
					/>
					<ReserveNowButton url={url} />
				</div>
			</div>
		</div>
	);
};

FacilityCard.propTypes = {
	name: PropTypes.string,
	layout: PropTypes.oneOf(["wide", "narrow"]),
	image: PropTypes.string,
	address: PropTypes.string,
	distance: PropTypes.number,
	url: PropTypes.string,
	startingPrice: PropTypes.number,
	lowestPrice: PropTypes.number,
	showDefaultImage: PropTypes.bool,
	reviews: PropTypes.shape({
		average: PropTypes.number,
		number: PropTypes.number
	}),
	schema: PropTypes.object,
	totalFees: PropTypes.number,
	lockFee: PropTypes.number,
	adminFee: PropTypes.number,
	showFeeWarning: PropTypes.bool,
	hasOnlineMoveins: PropTypes.bool,
	cardIndex: PropTypes.number,
	dealBannerText: PropTypes.string,
	subPageType: PropTypes.string
};

export default FacilityCard;
